import { Pencil, Trash2, Book } from 'lucide-react';
import type { KnowledgeBase } from '../../types/knowledge';
import type { Assistant } from '../../types/api';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  Badge,
  EmptyState,
} from '../ui';

interface KnowledgeBaseListProps {
  entries: KnowledgeBase[];
  assistants: Assistant[];
  onEdit: (entry: KnowledgeBase) => void;
  onDelete: (entry: KnowledgeBase) => void;
  isLoading?: boolean;
  isFiltered?: boolean;
}

function KnowledgeBaseList({ entries, assistants, onEdit, onDelete, isLoading, isFiltered }: KnowledgeBaseListProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <EmptyState
        icon={<Book className="h-12 w-12" />}
        title={isFiltered ? 'Nenhuma entrada encontrada' : 'Nenhuma entrada disponível'}
        description={
          isFiltered
            ? 'Tente ajustar os filtros para encontrar o que procura'
            : 'Crie sua primeira entrada na base de conhecimento para começar'
        }
      />
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Categoria</TableHead>
            <TableHead>Tags</TableHead>
            <TableHead>Assistentes</TableHead>
            <TableHead className="relative">
              <span className="sr-only">Ações</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map((entry: KnowledgeBase) => (
            <TableRow key={entry.id}>
              <TableCell>
                <div>
                  <div className="font-medium">{entry.name}</div>
                  {entry.description && (
                    <div className="text-sm text-gray-500">{entry.description}</div>
                  )}
                </div>
              </TableCell>
              <TableCell>
                {entry.category ? (
                  <Badge variant="info">{entry.category}</Badge>
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {entry.tags?.map(tag => (
                    <Badge key={tag} variant="default">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {entry.assistant_ids?.map(id => {
                    const assistant = assistants?.find((a: Assistant) => a.id === id);
                    return assistant ? (
                      <Badge key={id} variant="success">
                        {assistant.name}
                      </Badge>
                    ) : null;
                  })}
                </div>
              </TableCell>
              <TableCell className="text-right space-x-2">
                <Button variant="ghost" onClick={() => onEdit(entry)} title="Editar">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" onClick={() => onDelete(entry)} title="Excluir">
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default KnowledgeBaseList;
